import { TechlogFlightStatus } from '../../../../types/techLog/sector';
import type { TechLogToRusadaFlightInput } from './validation';

/**
 * Path-specific mock data for TechLog → Rusada flight transformation
 * This object matches the exact shape required by the inputZod schema and path-specific context type
 * Uses realistic aviation data with proper TechLog sector details and Rusada mapping information
 */
export const mockData: TechLogToRusadaFlightInput = {
  techlog: {
    sector: {
      id: 'TLS-2024-001234',
      tripId: 'TLT-2024-5678',
      preflightSignatureId: 'TL-SIG-PRE-001',
      postflightSignatureId: 'TL-SIG-POST-001',
      departureAirportId: 1001,
      arrivalAirportId: 1002,
      actualArrivalAirportId: 1002,
      pilotInCommandId: 'TL-PILOT-CMD-001',
      secondInCommandId: 'TL-PILOT-FO-001',
      pilotPerformingTakeoffId: 'TL-PILOT-CMD-001',
      pilotPerformingLandingId: 'TL-PILOT-CMD-001',
      userId: 'TL-USER-001',
      apuSeconds: 1800,
      apuReading: 1250.5,
      apuCyclesReading: 125,
      hobbsDeparture: 8745.2,
      hobbsArrival: 8747.6,
      offblocksTime: '2024-01-20T09:15:00Z',
      takeoffTime: '2024-01-20T09:45:00Z',
      landingTime: '2024-01-20T11:55:00Z',
      onblocksTime: '2024-01-20T12:10:00Z',
      landingsCount: 1,
      takeoffsCount: 1,
      status: TechlogFlightStatus.COMPLETE,
      flightType: 'PASSENGER',
      remarks: 'Normal flight operations. No issues reported.',
      createdAt: '2024-01-20T08:30:00Z',
      updatedAt: '2024-01-20T12:30:00Z',
      date: '2024-01-20',
      source: 'TECHLOG',
      preflightCustomData: {
        weatherConditions: 'Clear skies, light winds',
        fuelOnBoard: 12500.0,
        passengerCount: 142,
      },
      postflightCustomData: {
        fuelRemaining: 4200.0,
        maintenanceIssues: null,
        delayReasons: null,
        sortie_profile_code: '3_Phenom SPC 03',
        aero: 125, // Aero hours in minutes
      },
      cycles: {
        engine1: 8524,
        engine2: 8523,
        apu: 1876,
      },
      passengersMale: 68,
      passengersFemale: 74,
      // passengersTotal removed - not part of TechLogSector interface
      passengersInfants: 3,
      passengersChildren: 12,
      // cargoWeight removed - not part of TechLogSector interface
      baggageWeight: 3200.0,
      gpuStart: '00:00:00',
      gpuEnd: '01:30:00',
      gpuSeconds: 5400,
      maxFlightLevel: 39000,
      pressurisedCycle: true,
      preflightMxCheckIds: [],
      abandonmentReason: '',
      attachments: [],
    },
    trip: {
      id: 'TLT-2024-5678',
      aircraftId: 'TL-AC003',
      pilotInCommandId: 'TL-PILOT-CMD-001',
      secondInCommandId: 'TL-PILOT-FO-001',
      number: 456,
      callsign: 'BAW456',
      // status field removed - not part of TechLogTrip interface
      tripCategory: 'SCHEDULED',
      date: '2024-01-20',
      srpNumber: 456,
      attachments: [],
      isComplete: true,
      signatureId: 'TL-SIG-TRIP-001',
      completionSignatureId: 'TL-SIG-COMP-001',
      customData: {
        trendMonitoring: {
          justifications: {},
        },
      },
      justificationDescription: 'Routine scheduled service',
      abandonmentReason: '',
      createdAt: '2024-01-15T10:00:00Z',
      updatedAt: '2024-01-20T12:30:00Z',
    },
    additionalCrew: [
      {
        id: 'TL-CREW-001',
        tripId: 'TLT-2024-5678',
        personId: 'TL-PERSON-003',
        personRoleId: 'TL-ROLE-FA',
      },
    ],
    aircraft: {
      id: 'TL-AC003',
      registration: 'G-ZBKA',
      serialNumber: '60276',
      aircraftTypeId: 'TL-AT003',
      operatorId: 'TL-OP001',
      year: 2015,
      fuelUnit: 'kg',
      oilUnit: 'ml',
      maxFuelValue: 23000,
      monthlyCycles: 120,
      monthlyFlightSeconds: 360000,
      monthlyApuSeconds: 7200,
      maintenanceType: 'hours',
      apuMode: 'installed',
      flightStatus: 'onGround',
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-20T08:00:00Z',
      aircraftType: {
        id: 'TL-AT003',
        engineType: 'jet',
        engineCount: '2',
        weightCategory: 'heavy',
      },
    },
  },
  rusada: {
    registration: {
      id: 42,
      assetId: 187,
      registration: 'G-ZBKA',
      statusId: 1,
      status: 'ACTIVE',
      serialNo: '60276',
      operator: 'British Airways',
      isLocked: false,
    },
    techLogCategories: [
      {
        id: 1,
        category: 'Tech Log',
        isTechLog: true,
        isMaintenanceEntry: true,
        isCabinLog: false,
      },
    ],
    flightTypes: [
      {
        id: 32,
        type: 'Phenom SPC 03',
        description: 'Simulated Asymmetric Flight',
        positioningFlight: false,
        trainingFlight: false,
        newJourneyDefault: false,
      },
      {
        id: 29,
        type: 'Phenom SPC 02',
        description: 'General Handling',
        positioningFlight: false,
        trainingFlight: false,
        newJourneyDefault: false,
      },
      {
        id: 41,
        type: 'Phenom SPC 01',
        description: 'Circuits',
        positioningFlight: false,
        trainingFlight: false,
        newJourneyDefault: false,
      },
    ],
    airportDeparture: {
      id: 2001,
      place: 'LHR',
      placeName: 'London Heathrow Airport',
      placeRemarks: 'Major international hub',
      timeZoneId: 1,
      iataCode: 'LHR',
      icaoCode: 'EGLL',
      lHotelIdDefault: 0,
      oHotelIdDefault: 0,
      colour: 0,
    },
    airportArrival: {
      id: 2002,
      place: 'LAX',
      placeName: 'Los Angeles International Airport',
      placeRemarks: 'Major west coast hub',
      timeZoneId: 2,
      iataCode: 'LAX',
      icaoCode: 'KLAX',
      lHotelIdDefault: 0,
      oHotelIdDefault: 0,
      colour: 0,
    },
    airportActualArrival: {
      id: 2002,
      place: 'LAX',
      placeName: 'Los Angeles International Airport',
      placeRemarks: 'Major west coast hub',
      timeZoneId: 2,
      iataCode: 'LAX',
      icaoCode: 'KLAX',
      lHotelIdDefault: 0,
      oHotelIdDefault: 0,
      colour: 0,
    },
  },
};
